import * as React from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import classnames from 'classnames';
import { PrimaryButton } from '@fluentui/react';
import { AuftragStatusLabel, getAuftragLabel } from 'utils/labels';
import { formStyles } from 'styles/formStyles';
import { Auftrag } from 'api/auftrag';
import { Timestamps } from 'formComponents/Timestamps';
import { AuftragPosition } from 'api/auftragPosition';
import {
    araRegelungenByArtSelector,
    betreuerByNrSelector,
    laenderByIkz2Selector,
    skontodefinitionenByArtSelector,
    textbausteineByKzNrSelector,
} from 'store/lookups/selector';
import { formatMoney, formatPercent, formatSmDate } from 'utils/formatter';
import { authSelector } from 'store/auth/selector';
import nl2br from 'react-nl2br';
import { Termin } from 'api/termin';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { AuftragPositionen } from './AuftragPositionen';

const styles = mergeStyleSets({
    shortLine: {
        marginTop: 5,
        display: 'grid',
        gridTemplateColumns: '120px 120px 1fr 100px max-content',
        gridColumnGap: 10,
    },
    status: {
        textAlign: 'right',
    },
});

interface AuftragItemProps {
    auftrag: Auftrag;
    auftragPositionen: AuftragPosition[];
    wiedervorlagen: Termin[];
    withKunde?: boolean;
    viewMode: 'short' | 'long';
    withHeader?: boolean;
    editAuftrag: () => void;
    refetch: () => void;
}

export const AuftragItem: React.FC<AuftragItemProps> = ({
    auftrag,
    auftragPositionen,
    wiedervorlagen,
    withKunde,
    viewMode,
    withHeader = true,
    editAuftrag,
    refetch,
}) => {
    const { kunde } = auftrag;

    const auth = useSelector(authSelector);
    const betreuerByNr = useSelector(betreuerByNrSelector);
    const textbausteineByKzNr = useSelector(textbausteineByKzNrSelector);
    const skontodefinitionenByArt = useSelector(skontodefinitionenByArtSelector);
    const araRegelungenByArt = useSelector(araRegelungenByArtSelector);
    const laenderByIkz2 = useSelector(laenderByIkz2Selector);

    const history = useHistory();

    if (!kunde) {
        return null;
    }

    const renderTimestamps = () => (
        <Timestamps
            createdDate={auftrag.anlageDat}
            createdDecimalTime={auftrag.anlageUhr}
            createdUser={auftrag.anlageUser}
            modifiedDate={auftrag.updDat}
            modifiedDecimalTime={auftrag.updUhr}
            modifiedUser={auftrag.updUser}
        />
    );

    const renderStatus = () => <div className={styles.status}>{AuftragStatusLabel[auftrag.status]}</div>;

    const renderEditButton = () => (
        <div>
            {(auth.rolle === 'GF' || auth.rolle === 'ID' || auth.rolle === 'EX') && (
                <PrimaryButton
                    className={viewMode === 'short' ? formStyles.smallButton : ''}
                    onClick={() => {
                        editAuftrag();
                    }}
                    text="Bearbeiten"
                />
            )}
        </div>
    );

    const containerClassName = classnames(viewMode === 'short' ? formStyles.listItemShort : formStyles.listItem, {
        [formStyles.auftragInArbeit]: auftrag.status === 0 || auftrag.status === 9,
        [formStyles.auftragVersendet]: auftrag.status === 1 || auftrag.status === 7 || auftrag.status === 13,
        [formStyles.auftragAbgeschlossen]: auftrag.status === 4,
    });

    if (viewMode === 'short') {
        return (
            <div className={containerClassName}>
                <div className={styles.shortLine}>
                    <div className={formStyles.shortValue}>
                        <strong>
                            {auftrag.nr} {auftrag.abrufZu ? '(Abr)' : ''}
                            {auftrag.rahmen ? '(Ra)' : ''}
                        </strong>
                    </div>
                    <div className={formStyles.shortValue}>
                        <strong>{formatSmDate(auftrag.datum)}</strong>
                    </div>

                    {withKunde ? (
                        <div
                            className={classnames(formStyles.listTitleShort, formStyles.clickable)}
                            onClick={() => {
                                history.push('/kunden/' + kunde.nr);
                            }}
                        >
                            {`${kunde.name1} ${kunde.name2} (${kunde.nr})`}
                        </div>
                    ) : (
                        <div />
                    )}

                    {renderStatus()}
                    {renderEditButton()}
                </div>

                <div>
                    <AuftragPositionen
                        kunde={kunde}
                        auftrag={auftrag}
                        auftragPositionen={auftragPositionen}
                        wiedervorlagen={wiedervorlagen}
                        viewMode={viewMode}
                        withHeader={withHeader}
                        refetch={refetch}
                    />
                </div>
            </div>
        );
    }

    return (
        <>
            <div className={containerClassName}>
                <div className={formStyles.topBlock}>
                    <div>
                        <div
                            className={classnames(formStyles.listTitle, formStyles.clickable)}
                            onClick={() => {
                                history.push('/kunden/' + kunde.nr);
                            }}
                        >
                            {getAuftragLabel(auftrag, kunde)}
                        </div>
                    </div>
                    {renderStatus()}
                    {renderEditButton()}
                </div>
                <div className={formStyles.fourColumnsBlock}>
                    <div className={formStyles.firstColumn}>
                        <div className={formStyles.line}>
                            {kunde.plz} {kunde.ort}, {kunde.strasse} {kunde.land && `(${kunde.land})`}
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Ansprechpartner:</div>
                            <div className={formStyles.dataValue}>{auftrag.kaName}</div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Auftrag-Nr:</div>
                            <div className={formStyles.dataValue}>
                                <strong>{auftrag.nr}</strong>
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Angebot-Nr:</div>
                            <div className={formStyles.dataValue}>
                                <strong>{auftrag.angebotNr}</strong>
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Service-Nr:</div>
                            <div className={formStyles.dataValue}>
                                <strong>{auftrag.serviceNr}</strong>
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Abruf zu:</div>
                            <div className={formStyles.dataValue}>
                                <strong>{auftrag.abrufZu}</strong>
                            </div>
                        </div>
                        {/*
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Teilrechnung:</div>
                            <div className={formStyles.dataValue}>{auftrag.teilR ? 'Ja' : 'Nein'}</div>
                        </div>
                        */}
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Rahmenauftrag:</div>
                            <div className={formStyles.dataValue}>{auftrag.rahmen ? 'Ja' : 'Nein'}</div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Betreuerumsatz:</div>
                            <div className={formStyles.dataValue}>{auftrag.betrUm ? 'Ja' : 'Nein'}</div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Ausstellungsdat.:</div>
                            <div className={formStyles.dataValue}>
                                <strong>{formatSmDate(auftrag.datum)}</strong>
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Auftrag gültig bis:</div>
                            <div className={formStyles.dataValue}>
                                <strong>{formatSmDate(auftrag.datBis)}</strong>
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Betreuer:</div>
                            <div className={formStyles.dataValue}>
                                <strong>
                                    {auftrag.betreuer &&
                                        (betreuerByNr[auftrag.betreuer]
                                            ? betreuerByNr[auftrag.betreuer].name || auftrag.betreuer
                                            : auftrag.betreuer)}
                                </strong>
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Tel.-Betreuer:</div>
                            <div className={formStyles.dataValue}>
                                <strong>
                                    {auftrag.telBetreuer
                                        ? betreuerByNr[auftrag.telBetreuer]
                                            ? betreuerByNr[auftrag.telBetreuer].name || auftrag.telBetreuer
                                            : auftrag.telBetreuer
                                        : '-'}
                                </strong>
                            </div>
                        </div>
                    </div>
                    <div className={formStyles.secondColumn}>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKeyLine}>Text:</div>
                            <div className={formStyles.dataValue}>{nl2br(auftrag.txt)}</div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Textbaustein 1:</div>
                            <div className={formStyles.dataValue}>
                                {auftrag.txtB1
                                    ? textbausteineByKzNr[22][auftrag.txtB1]
                                        ? textbausteineByKzNr[22][auftrag.txtB1].name || auftrag.txtB1
                                        : auftrag.txtB1
                                    : '-'}
                            </div>
                        </div>
                        {auftrag.lieferAdr && (
                            <div className={formStyles.keyValueLine}>
                                <div className={formStyles.dataKey120}>Lieferadresse:</div>
                                <div className={formStyles.dataValue}>
                                    {`${auftrag.lieferAdr.name1} ${auftrag.lieferAdr.name2}`}
                                    <br />
                                    {auftrag.lieferAdr.plz} {auftrag.lieferAdr.ort}
                                    {', '}
                                    {auftrag.lieferAdr.strasse}
                                    {auftrag.lieferAdr.land &&
                                        ', ' +
                                            (auftrag.lieferAdr.land ? laenderByIkz2[auftrag.lieferAdr.land]?.text : '')}
                                </div>
                            </div>
                        )}
                        {auftrag.lieferKunde && (
                            <div className={formStyles.keyValueLine}>
                                <div className={formStyles.dataKey120}>Lieferkunde:</div>
                                <div className={formStyles.dataValue}>
                                    {auftrag.lieferKunde.name1} {auftrag.lieferKunde.name2}
                                </div>
                            </div>
                        )}
                        {auftrag.rechnungsAdr && (
                            <div className={formStyles.keyValueLine}>
                                <div className={formStyles.dataKey120}>Rechnungsadresse:</div>
                                <div className={formStyles.dataValue}>
                                    {`${auftrag.rechnungsAdr.name1} ${auftrag.rechnungsAdr.name2}`}
                                    <br />
                                    {auftrag.rechnungsAdr.plz} {auftrag.rechnungsAdr.ort}
                                    {', '}
                                    {auftrag.rechnungsAdr.strasse}
                                    {auftrag.rechnungsAdr.land &&
                                        ', ' +
                                            (auftrag.rechnungsAdr.land
                                                ? laenderByIkz2[auftrag.rechnungsAdr.land]?.text
                                                : '')}
                                </div>
                            </div>
                        )}
                        {auftrag.rechnungsKunde && (
                            <div className={formStyles.keyValueLine}>
                                <div className={formStyles.dataKey120}>Rechnungskunde:</div>
                                <div className={formStyles.dataValue}>
                                    {auftrag.rechnungsKunde.name1} {auftrag.rechnungsKunde.name2}
                                </div>
                            </div>
                        )}
                    </div>
                    <div className={formStyles.thirdColumn}>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Lieferbed.:</div>
                            <div className={formStyles.dataValue}>
                                {auftrag.liefBed
                                    ? textbausteineByKzNr[11][auftrag.liefBed]
                                        ? textbausteineByKzNr[11][auftrag.liefBed].name || auftrag.liefBed
                                        : auftrag.liefBed
                                    : '-'}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Lieferzeit:</div>
                            <div className={formStyles.dataValue}>
                                {auftrag.liefZeit
                                    ? textbausteineByKzNr[14][auftrag.liefZeit]
                                        ? textbausteineByKzNr[14][auftrag.liefZeit].name || auftrag.liefZeit
                                        : auftrag.liefZeit
                                    : '-'}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Lieferdatum.:</div>
                            <div className={formStyles.dataValue}>
                                {auftrag.liefDat && (formatSmDate(auftrag.liefDat, true) || '-')}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Liefer-KW.:</div>
                            <div className={formStyles.dataValue}>{auftrag.liefKw ? auftrag.liefKw : '-'}</div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Zahlungsbed.:</div>
                            <div className={formStyles.dataValue}>
                                {auftrag.zahlBed
                                    ? skontodefinitionenByArt[auftrag.zahlBed]
                                        ? skontodefinitionenByArt[auftrag.zahlBed].text || auftrag.zahlBed
                                        : auftrag.zahlBed
                                    : '-'}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>ARA Regelung:</div>
                            <div className={formStyles.dataValue}>
                                {auftrag.ara
                                    ? araRegelungenByArt[auftrag.ara]
                                        ? araRegelungenByArt[auftrag.ara].text || auftrag.ara
                                        : auftrag.ara
                                    : '-'}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Fracht Lager:</div>
                            <div className={formStyles.dataValue}>{formatMoney(auftrag.fracht)}</div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Fracht extern:</div>
                            <div className={formStyles.dataValue}>{formatMoney(auftrag.roadPr)}</div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Tipp-Prof.:</div>
                            <div className={formStyles.dataValue}>{formatPercent(auftrag.tipp)}</div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>MwSt:</div>
                            <div className={formStyles.dataValue}>{formatPercent(auftrag.mwstCode)}</div>
                        </div>
                    </div>
                    <div className={formStyles.fourthColumn}>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKeyLine}>Bemerkungen (beim Kunden):</div>
                            <div className={formStyles.dataValue}>{nl2br(auftrag?.kunde?.bemerkung)}</div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKeyLine}>Lieferbemerkung (beim Kunden):</div>
                            <div className={formStyles.dataValue}>{nl2br(auftrag?.kunde?.liefBem)}</div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKeyLine}>Zusatzvereinbarung:</div>
                            <div className={formStyles.dataValue}>{nl2br(auftrag?.zusatz)}</div>
                        </div>
                    </div>
                </div>
                <div>
                    <AuftragPositionen
                        kunde={kunde}
                        auftrag={auftrag}
                        auftragPositionen={auftragPositionen}
                        wiedervorlagen={wiedervorlagen}
                        viewMode={viewMode}
                        refetch={refetch}
                    />
                </div>
                <div className={formStyles.threeColumnsBlock}>
                    <div className={formStyles.firstColumn}>
                        <div className={formStyles.sumLine}>Summe Netto: {formatMoney(auftrag.sNetto)}</div>
                    </div>
                    <div className={formStyles.secondColumn}>
                        <div className={formStyles.sumLine}>Summe Brutto: {formatMoney(auftrag.sBrutto)}</div>
                    </div>
                    <div className={formStyles.thirdColumn}>
                        <div className={formStyles.sumLine}>Summe MwSt.: {formatMoney(auftrag.sMwst)}</div>
                    </div>
                </div>
                {renderTimestamps()}
            </div>
            {/* showCopy && (
                <KundeSelect
                    label="Kunde"
                    kNr={auftrag?.kNr}
                    name={kunde?.name1}
                    dialogOnly
                    onChange={async (newKNr) => {
                        if (auftrag && newKNr) {
                            await copyAuftrag(newKNr, auftrag, auftragPositionen);
                            refetch();
                            setShowCopy(false);
                        }
                    }}
                    onCancel={() => setShowCopy(false)}
                />
            ) */}
        </>
    );
};
